import { Dispatch, forwardRef, useMemo, useState } from 'react';
import { SearchSuggestionTabType } from '../../../AnalyticsContext/LinkTracking';
import { useFlags } from '../../../services/flags';
import { safeKeysOf } from '../../../util/safeKeysOf';
import { AllSuggestions } from './AllSuggestions/AllSuggestions';
import { AllSuggestionsBeta } from './AllSuggestions/AllSuggestionsBeta';
import { EmptySearchSuggestions } from './EmptySearchSuggestions/EmptySearchSuggestions';
import { FilterSuggestions } from './FilterSuggestions/FilterSuggestions';
import { PracticeSuggestions } from './PracticeSuggestions/PracticeSuggestions';
import { PractitionerSuggestions } from './PractitionerSuggestions/PractitionerSuggestions';
import { SuggestionTabLabel } from './SuggestionTabLabel';
import { ButtonBar } from './SuggestionsContainer/ButtonBar/ButtonBar';
import { SuggestionsContainer } from './SuggestionsContainer/SuggestionsContainer';
import { SuggestionsLayoutTemplate } from './SuggestionsLayoutTemplate/SuggestionsLayoutTemplate';
import { TreatmentSuggestions } from './TreatmentSuggestions/TreatmentSuggestions';
import { SuggestionData } from './types';

const BUTTONS: Record<
  SuggestionTabLabel,
  typeof PracticeSuggestions | typeof PractitionerSuggestions
> = {
  All: AllSuggestions,
  Specialties: FilterSuggestions,
  Practices: PracticeSuggestions,
  Practitioners: PractitionerSuggestions,
  Topics: TreatmentSuggestions,
};

const BETA_BUTTONS: Record<
  SuggestionTabLabel,
  typeof PracticeSuggestions | typeof PractitionerSuggestions
> = {
  All: AllSuggestionsBeta,
  Specialties: FilterSuggestions,
  Practices: PracticeSuggestions,
  Practitioners: PractitionerSuggestions,
  Topics: TreatmentSuggestions,
};

interface Props {
  searchTerm: string;
  onChoose: Dispatch<SuggestionData>;
  setValue?: Setter<string>;
  inline?: boolean;
  disableTabs?: boolean;
  enabledFilterOffset?: boolean;
}

const mapTabToTrackingValue: Record<SuggestionTabLabel, SearchSuggestionTabType> = {
  All: SearchSuggestionTabType.ALL,
  Specialties: SearchSuggestionTabType.SPECIALTY,
  Practices: SearchSuggestionTabType.PRACTICE,
  Practitioners: SearchSuggestionTabType.PRACTITIONER,
  Topics: SearchSuggestionTabType.Topics,
};

export const Suggestions = forwardRef<HTMLDivElement, Props>(
  ({ searchTerm, onChoose, setValue, inline, disableTabs = false, enabledFilterOffset }, ref) => {
    const buttonsToUse = useFlags().textSuggestions ? BETA_BUTTONS : BUTTONS;

    const buttonBarItems = useMemo(
      () => safeKeysOf(buttonsToUse).map(it => ({ label: it, value: it })),
      [buttonsToUse],
    );

    const [selectedButton, setSelectedButton] = useState<SuggestionTabLabel>('All');

    const ListComponent = buttonsToUse[selectedButton];

    const buttonBar = !disableTabs && (
      <ButtonBar items={buttonBarItems} value={selectedButton} onChange={setSelectedButton} />
    );

    const setSuggestionsValue = (text: string) => {
      setValue && setValue(text);
    };

    return (
      <SuggestionsContainer ref={ref} enabledFilterOffset={enabledFilterOffset} inline={inline}>
        {!searchTerm && !inline && <EmptySearchSuggestions setValue={setSuggestionsValue} />}
        {searchTerm && (
          <SuggestionsLayoutTemplate
            buttonBar={buttonBar}
            list={
              <ListComponent
                searchTerm={searchTerm}
                onChoose={onChoose}
                trackingData={{ searchSuggestionTab: mapTabToTrackingValue[selectedButton] }}
              />
            }
          />
        )}
      </SuggestionsContainer>
    );
  },
);
